import { useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", postcode: "", project_type: "extension", message: "" });
  const [files, setFiles] = useState([]);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("phone", form.phone);
      fd.append("postcode", form.postcode);
      fd.append("project_type", form.project_type);
      fd.append("message", form.message);
      fd.append("type", "contact");
      files.forEach(f => fd.append("files", f));
      await api.post("/enquiry/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSent(true);
      toast.success("Enquiry sent — we'll be in touch within one working day.");
    } catch (err) {
      toast.error("Could not send. Please try again or call us.");
    }
    setSubmitting(false);
  };
  return (
    <main data-testid="contact-page" className="px-6 md:px-12 lg:px-16 pt-16 pb-24">
      <div className="label-eyebrow mb-6">Contact / Enquiry</div>
      <h1 className="font-black tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-3xl">Talk to a builder, not a call centre.</h1>
      <div className="mt-16 grid grid-cols-1 lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2">
          {sent ? (
            <div className="de-card p-12">
              <div className="label-eyebrow mb-4">Thank you</div>
              <p className="text-lg">We've received your enquiry. Expect a reply within one working day.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="de-card p-8 md:p-12 space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" value={form.name} onChange={v=>setForm({...form,name:v})} testid="contact-name" required/>
                <Field label="Email" type="email" value={form.email} onChange={v=>setForm({...form,email:v})} testid="contact-email" required/>
                <Field label="Phone" value={form.phone} onChange={v=>setForm({...form,phone:v})} testid="contact-phone"/>
                <Field label="Postcode" value={form.postcode} onChange={v=>setForm({...form,postcode:v})} testid="contact-postcode"/>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">What are you planning?</label>
                <select data-testid="contact-project-type" value={form.project_type} onChange={e=>setForm({...form,project_type:e.target.value})}
                  className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]">
                  <option value="extension">Home extension</option>
                  <option value="loft">Loft conversion</option>
                  <option value="garage">Garage conversion</option>
                  <option value="new_build">New build</option>
                  <option value="other">Something else / not sure</option>
                </select>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Anything we should know?</label>
                <textarea data-testid="contact-message" required rows={6} value={form.message} onChange={e=>setForm({...form,message:e.target.value})}
                  className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]"/>
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Attach plans or documents (optional)</label>
                <p className="text-xs text-[#6B7280] mb-3">Images, PDFs or Word docs — useful for loft conversions, garage conversions, new builds or any project outside the instant quote. We'll be happy to quote for those works.</p>
                <label data-testid="contact-upload-label" className="de-btn-secondary cursor-pointer inline-flex">
                  {files.length ? `${files.length} file${files.length > 1 ? "s" : ""} selected` : "Choose files"}
                  <input type="file" multiple accept="image/*,application/pdf,.doc,.docx" className="hidden" data-testid="contact-file-input" onChange={e=>setFiles(Array.from(e.target.files || []))}/>
                </label>
                {files.length > 0 && <ul className="mt-3 text-xs text-[#6B7280] space-y-1">{files.map((f, i) => <li key={`${f.name}-${i}`}>· {f.name}</li>)}</ul>}
              </div>
              <button data-testid="contact-submit" disabled={submitting} className="de-btn-primary" type="submit">{submitting ? "Sending…" : "Send enquiry"}</button>
            </form>
          )}
        </div>
        <div className="de-card p-8">
          <div className="label-eyebrow mb-3">Direct</div>
          <div className="text-sm leading-relaxed">
            <span className="label-eyebrow block mb-1">Phone</span>
            <a href="tel:02034881081" data-testid="contact-phone-number" className="text-[#C79A3B] font-bold text-lg hover:underline">0203 488 1081</a><br/><br/>
            <span className="label-eyebrow block mb-1">Phone hours</span>
            Mon–Fri · 09:00–17:00
          </div>
          <div className="border-t border-[#E5E7EB] mt-6 pt-6">
            <span className="label-eyebrow block mb-1">Prefer to chat?</span>
            <p className="text-sm text-[#6B7280]">Ask <b>Daisy</b>, our assistant (bottom-right). She can answer your questions and, if you'd like, pass your enquiry straight to the team — no form needed.</p>
          </div>
        </div>
      </div>
    </main>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} required={required} value={value}
        onChange={e=>onChange(e.target.value)}
        className="w-full border border-[#E5E7EB] bg-white px-4 py-3 focus:outline-none focus:border-[#C79A3B]"/>
    </div>
  );
}
